import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import React, { useCallback, useState } from 'react';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import Autocomplete, {
  AutocompleteChangeReason,
} from '@mui/material/Autocomplete';
import { useVendorSearchQuery } from '../hooks/apiHook';
import debounce from '@mui/material/utils/debounce';
import { useSearchParams } from 'react-router-dom';

const DEBOUCE_DELAY = 500;

export default function VendorSearchBar(
  props: Readonly<{
    onVendorSelected: (vendor: any) => void;
    setVendor: React.Dispatch<React.SetStateAction<string | undefined>>;
  }>
) {
  const [searchParams] = useSearchParams();
  const vendorName = searchParams.get('vendorName');
  const countryCode = searchParams.get('countryCode');
  const [inputValue, setInputValue] = useState<string>(vendorName ?? '');
  const [searchQuery, setSearchQuery] = useState<string>(vendorName ?? '');
  const { data, error } = useVendorSearchQuery({
    countryCode: String(countryCode),
    vendorNameToSearch: searchQuery.trim(),
  });
  const options = data ?? [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateQuery = useCallback(
    debounce((query) => setSearchQuery(query), DEBOUCE_DELAY),
    []
  );
  const handleInputChange = (event: React.SyntheticEvent, value: string) => {
    setInputValue(value);
    updateQuery(value);
  };
  const handleSearchClick = (value: string) => {
    const vendor = data?.find(
      (v) => value.toLocaleUpperCase() === v.vendorName.toLocaleUpperCase()
    );
    if (vendor && !error) {
      props.onVendorSelected(vendor);
    }
    if (error) props.setVendor(value);
  };
  const handleKeyDown = (event: any) => {
    if (event.keyCode === 13) {
      handleSearchClick(event.target.defaultValue);
    }
  };
  const onChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: string | null,
    reason: AutocompleteChangeReason
  ) => {
    if (reason === 'selectOption') handleSearchClick(value ?? '');
    if (reason === 'clear') props.setVendor(undefined);
  };
  return (
    <Stack direction={'row'} flexWrap="wrap" gap={2} mb={4.5}>
      <FormControl sx={{ minWidth: 250, width: '564px' }}>
        <Autocomplete
          data-testid="gds-vp-search-bar"
          disablePortal
          filterOptions={(options) => options}
          freeSolo
          getOptionLabel={(lable: any) => {
            return typeof lable === 'string' ? lable : `${lable.vendorName}`;
          }}
          inputValue={inputValue}
          onChange={onChange}
          onInputChange={handleInputChange}
          onKeyDown={handleKeyDown}
          options={options.map((vendor) => vendor.vendorName)}
          renderInput={(params) => (
            <TextField
              {...params}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              data-testid="gds-vp-textfield"
              inputProps={{
                ...params.inputProps,
                'data-testid': 'gds_vendor_program_input',
              }}
              placeholder={'Enter vendor full name'}
            />
          )}
          sx={(theme) => ({
            '& .MuiTextField-root .MuiOutlinedInput-root': {
              '.MuiAutocomplete-input': {
                padding: `${theme.spacing(0.75)} 0 ${theme.spacing(0.75)}`,
              },
              borderRadius: `${theme.shape.borderRadius}px`,
            },
          })}
        />
      </FormControl>
    </Stack>
  );
}
